import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons/faArrowRotateRight";
import { TFunction } from "i18next";
import { omitBy } from "lodash";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../../../components/Button";
import { AtiraSelect } from "../../../../components/AtiraSelect";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { SubHeader } from "../../../../components/SubHeader";
import { AtiraTable } from "../../../../components/table/AtiraTable";
import { WarningModal } from "../../../../components/WarningModal";
import { SearchUsersDto } from "../../../../model/admin/dto/SearchUsersDto";
import { SearchUsersUserDto } from "../../../../model/admin/dto/SearchUsersUserDto";
import { UserKind } from "../../../../model/user/types/UserKind.enum";
import { adminSliceSelectors } from "../../../../redux/admin/admin.selector";
import { adminActions } from "../../../../redux/admin/admin.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { AdminMessageModal } from "../../components/AdminMessageModal";
import { AdminUserThresholdsUpdateModal } from "../../components/AdminUserThresholdsUpdateModal";
import { createAdminSearchUsersTableColumns } from "../../components/createAdminSearchUsersTableColumns";
import { Flex } from "../../../../components/Flex";
import { Tooltip } from "../../../../utils/local-antd";
import { Input } from "../../../../components/Input";
import { PageIcons } from "../../../../utils/GlobalIcons";
import { openImpersonationTab } from "../../../../utils/openImpersonationTab";
import { ImpersonationKind } from "../../../../model/user/types/ImpersonationKind.enum";

const StyledIconButton = styled(Button)<{ disabled?: boolean }>`
  background-color: transparent;
  padding: 0;
  margin: 0;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const StyledSelect = styled(AtiraSelect)`
  width: 8rem;
`;

const TableWrapper = styled(Flex)`
  .actionable-cell {
    background-color: ${({ theme }) => theme.main} !important;

    p {
      color: ${({ theme }) => theme.white} !important;
    }

    svg {
      color: ${({ theme }) => theme.main} !important;
    }
  }
`;

const getUserKindFilterOptions = (t: TFunction) => [
  { label: t("common.all_kinds"), value: null },
  { label: t(`common.${UserKind.FREE.toLowerCase()}`), value: UserKind.FREE },
  { label: t(`common.${UserKind.PAID.toLowerCase()}`), value: UserKind.PAID },
];

const AdminSearchUsers: React.FC = () => {
  const [selectedUser, setSelectedUser] =
    useState<Partial<SearchUsersUserDto> | null>(null);
  const [
    toggleUserStatusUserModalVisible,
    settoggleUserStatusUserModalVisible,
  ] = useState(false);
  const [
    updateUserThresholdsModalVisible,
    setUpdateUserThresholdsModalVisible,
  ] = useState(false);
  const [enableDisableLoading, setEnableDisableLoading] = useState(false);
  const [sendMessageToUserModalVisible, setSendMessageToUserModalVisible] =
    useState(false);

  const { control, getValues } = useForm<SearchUsersDto>();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const users = useAppSelector(adminSliceSelectors.selectUsers);
  const searchLoading = useAppSelector(adminSliceSelectors.searchUsersLoading);
  const searchMetaPageSize = useAppSelector(
    adminSliceSelectors.selectSearchUsersTablePageSize,
  );
  const searchMetaPage = useAppSelector(
    adminSliceSelectors.selectSearchUsersTablePage,
  );
  const searchUserMeta = useAppSelector(adminSliceSelectors.selectUsersMeta);

  const onToggleUserStatus = (user: typeof selectedUser) => {
    setSelectedUser(user);
    settoggleUserStatusUserModalVisible(true);
  };

  const onUpdateThresholds = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setUpdateUserThresholdsModalVisible(true);
  };

  const onSendMessageToUser = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setSendMessageToUserModalVisible(true);
  };

  const onSignInAsUser = async (user: typeof selectedUser) => {
    try {
      const { token, user: supportUser } = await dispatch(
        adminActions.createSupportSession({ userId: user!._id! }),
      ).unwrap();

      openImpersonationTab(
        token,
        supportUser,
        ImpersonationKind.SUPPORT,
        t("settings.manageAccounts.action.switchAccount.popupBlocked"),
      );
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const tableColumns = createAdminSearchUsersTableColumns<SearchUsersUserDto>({
    t,
    toggleStatus: onToggleUserStatus,
    updateThresholds: onUpdateThresholds,
    sendMessage: onSendMessageToUser,
    signInAsUser: onSignInAsUser,
  });

  const enableDisableUser = async () => {
    try {
      setEnableDisableLoading(true);

      await dispatch(
        adminActions.toggleUserStatus({ userId: selectedUser!._id! }),
      ).unwrap();

      fetchUsers();

      AtiraToast.success(
        t(
          `admin.user.${selectedUser?.enabled === false ? "enable" : "disable"}.success`,
          { name: selectedUser?.name || selectedUser?.email },
        ),
      );
      settoggleUserStatusUserModalVisible(false);
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setEnableDisableLoading(false);
    }
  };

  const fetchUsers = useCallback(
    () =>
      dispatch(
        adminActions.searchUsers(
          omitBy(
            {
              userId,
              keyword: getValues("keyword"),
              kind: getValues("kind"),
              meta: {
                page: searchMetaPage - 1,
                count: searchMetaPageSize,
              },
            },
            (value) => !value,
          ) as unknown as SearchUsersDto,
        ),
      ),
    [dispatch, userId, getValues, searchMetaPage, searchMetaPageSize],
  );

  const onTableShowSize = (currPage: number, newSize: number) => {
    dispatch(adminActions.setSearchUsersTablePageSize(newSize));
    onPageChange(1, newSize);
  };

  const debouncedSearch = useCallback(
    debounce(() => {
      dispatch(adminActions.setCurrentSearchUsersPage(1));
      fetchUsers();
    }, 1000),
    [fetchUsers],
  );

  const onFilter = async () => {
    try {
      await fetchUsers();
    } catch (e: any) {
      AtiraToast.apiError(e);
    }
  };

  const onPageChange = async (page: number, pageSize: number) => {
    dispatch(adminActions.setCurrentSearchUsersPage(page));
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Flex flexDirection="column" flex={1}>
      <SubHeader title={t("common.users")} icon={PageIcons.AdminSearchUsers} />

      <Flex flexDirection="column" gap="m" padding="m" flex={1}>
        <Flex gap="s">
          <StyledIconButton
            backgroundColor="gray"
            onClick={fetchUsers}
            disabled={searchLoading}
          >
            <Tooltip title={t("common.refresh")}>
              <AtiraIcon icon={faArrowRotateRight} color="main" size="2x" />
            </Tooltip>
          </StyledIconButton>

          <Controller
            control={control}
            name="keyword"
            render={({ field: { value, onChange } }) => (
              <Input
                width="15rem"
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                  debouncedSearch();
                }}
                placeholder={t("common.search")}
              />
            )}
          />

          <Controller
            control={control}
            name="kind"
            render={({ field: { value, onChange } }) => (
              <StyledSelect
                options={getUserKindFilterOptions(t)}
                defaultValue={getUserKindFilterOptions(t)[0]}
                value={value}
                onChange={(e) => {
                  onChange(e);
                  onFilter();
                }}
              />
            )}
          />
        </Flex>

        <TableWrapper>
          <AtiraTable
            key={JSON.stringify(tableColumns)}
            data={users}
            columns={tableColumns}
            loading={searchLoading}
            pagination={{
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              pageSize: searchMetaPageSize,
              current: searchMetaPage,
              total: searchUserMeta?.total,
              onChange: onPageChange,
              showSizeChanger: true,
              onShowSizeChange: onTableShowSize,
            }}
            rowSelection={undefined}
          />
        </TableWrapper>
      </Flex>

      <WarningModal
        title={t(
          `admin.user.${(selectedUser as any)?.enabled ? "disable" : "enable"}.modal.title`,
          { name: selectedUser?.name || selectedUser?.email },
        )}
        description={t(
          `admin.user.${(selectedUser as any)?.enabled ? "disable" : "enable"}.modal.description`,
          { name: selectedUser?.name || selectedUser?.email },
        )}
        isOpen={toggleUserStatusUserModalVisible}
        onClose={() => settoggleUserStatusUserModalVisible(false)}
        onConfirm={enableDisableUser}
        loading={enableDisableLoading}
      />

      <AdminUserThresholdsUpdateModal
        user={selectedUser}
        open={updateUserThresholdsModalVisible}
        onClose={() => {
          fetchUsers();
          setUpdateUserThresholdsModalVisible(false);
        }}
      />

      <AdminMessageModal
        user={selectedUser}
        open={sendMessageToUserModalVisible}
        onClose={() => setSendMessageToUserModalVisible(false)}
      />
    </Flex>
  );
};

export default AdminSearchUsers;
